/* eslint-disable import/extensions */

import React from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import {
	AgentProfileCreator,
	getAgentCreator,
} from '@atlaskit/rovo-agent-components/ui/AgentProfileInfo';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { getAAIDFromARI, useProfileResolution } from '../../../utils/agents/index.tsx';
import { isCustomerAgent, isSystemAgent } from '../../types.ts';
import { getAtlasURL } from '../../utils/atlas-url/index.ts';
import { useAgentCrossProductUrlWrapper } from '../../utils/use-agent-cross-product-url/use-agent-cross-product-url.tsx';

import type { creator_AtlassianConversationAssistantAgent_AgentCreatorDataComponent$key } from './__generated__/creator_AtlassianConversationAssistantAgent_AgentCreatorDataComponent.graphql.ts';

/**
 * Relay component to render agent creator
 */
export const AgentCreatorDataComponent = ({
	agentRef,
	onCreatorLinkClick,
	siteId,
}: {
	agentRef: creator_AtlassianConversationAssistantAgent_AgentCreatorDataComponent$key | null;
	onCreatorLinkClick: () => void;
	siteId: string;
}): React.JSX.Element => {
	di(AgentProfileCreator, useAgentCrossProductUrlWrapper);

	const wrapCrossProductUrl = useAgentCrossProductUrlWrapper({
		xpcBridge: 'rovoAgents-agentProfileModal',
	});

	const agentFragment =
		useFragment<creator_AtlassianConversationAssistantAgent_AgentCreatorDataComponent$key>(
			graphql`
				fragment creator_AtlassianConversationAssistantAgent_AgentCreatorDataComponent on AgentStudioAssistant {
					creatorType
					creator {
						accountId
						name
						... on AtlassianAccountUser {
							accountStatus
						}
					}
					forgeCreator
					authoringTeam {
						displayName
						profileUrl
					}
				}
			`,
			agentRef,
		);

	const creator = getAgentCreator({
		creatorType: agentFragment?.creatorType ?? '',
		authoringTeam: agentFragment?.authoringTeam
			? {
					displayName: agentFragment.authoringTeam.displayName ?? '',
					profileLink: agentFragment.authoringTeam.profileUrl ?? undefined,
				}
			: undefined,
		userCreator: agentFragment?.creator
			? {
					name: agentFragment.creator.name ?? '',
					status: agentFragment.creator.accountStatus,
					profileLink: agentFragment.creator.accountId
						? wrapCrossProductUrl(
								getAtlasURL(siteId, `/people/${agentFragment.creator.accountId}`, undefined),
							)
						: undefined,
				}
			: undefined,
		forgeCreator: agentFragment?.forgeCreator ?? undefined,
	});

	return (
		<AgentProfileCreator
			creator={creator}
			isLoading={false}
			onCreatorLinkClick={onCreatorLinkClick}
		/>
	);
};

/**
 * Use `AgentCreatorDataComponent` as much as possible
 * This component is used only if there is no access to Relay as last resort
 * Only internal use for this package, do not export
 * if you need this component, use `AgentProfileCreator` from `@atlaskit/rovo-agent-components` instead
 *
 * @deprecated use `AgentCreatorDataComponent` instead
 */
export const AgentCreator = ({
	agent,
	siteId,
	onCreatorLinkClick,
	authoringTeam,
	forgeCreator,
}: {
	agent: Agent;
	siteId: string;
	onCreatorLinkClick: () => void;
	authoringTeam?: {
		displayName: string;
		profileUrl: string;
	};
	forgeCreator?: string;
}): React.JSX.Element => {
	di(useProfileResolution, AgentProfileCreator, useAgentCrossProductUrlWrapper);
	const wrapCrossProductUrl = useAgentCrossProductUrlWrapper({
		xpcBridge: 'rovoAgents-agentProfileModal',
	});
	const userId =
		isCustomerAgent(agent) && agent.creator ? getAAIDFromARI(agent.creator) : undefined;
	const shouldFetchCreator = Boolean(!isSystemAgent(agent) && userId);
	const { profile } = useProfileResolution(userId as string, siteId, shouldFetchCreator);

	const creator = getAgentCreator({
		creatorType: agent.creator_type ?? '',
		authoringTeam: authoringTeam
			? {
					displayName: authoringTeam.displayName ?? '',
					profileLink: authoringTeam.profileUrl ?? undefined,
				}
			: undefined,
		userCreator: userId
			? {
					name: profile.data?.fullName ?? '',
					status: profile.data?.status,
					profileLink: wrapCrossProductUrl(getAtlasURL(siteId, `/people/${userId}`, undefined)),
				}
			: undefined,
		// agent.creator is only applicable for REST endpoint
		forgeCreator: forgeCreator ?? agent.creator ?? undefined,
	});

	return (
		<AgentProfileCreator
			creator={creator}
			isLoading={shouldFetchCreator ? profile.isFetching : false}
			onCreatorLinkClick={onCreatorLinkClick}
		/>
	);
};
